/**
 * Service Worker registrieren und neue Versionen in offene Tabs bringen — ohne Eingaben zu
 * verwerfen.
 *
 * Vorgeschichte: Clients hingen über mehrere Deploys auf alten Versionen (sw.js kam „immutable",
 * kein Reload beim SW-Wechsel). Die Antwort darauf war skipWaiting beim Install plus Reload bei
 * jedem controllerchange. Damit reichte es, nach einem Deploy zurück in den Tab zu wechseln, und
 * der halbe Kommentar war weg (bis v1.340.0).
 *
 * Jetzt:
 * - Der neue Worker wartet (workers/service-worker.ts), bis dieser Tab einen sicheren Moment
 *   findet: Seitenwechsel, Tab im Hintergrund, sichtbarer Tab lange unberührt (./controller.ts).
 * - Neu geladen wird nur, wenn nichts in Arbeit ist (./guards.ts).
 * - Nach neuen Versionen gesucht wird beim Laden, bei Rückkehr in den Tab und regelmäßig;
 *   updateViaCache 'none' holt sw.js nie aus dem HTTP-Cache (ergänzt nginx).
 */
import { createAppUpdateController, type AppUpdateController } from './controller';
import {
  createUnsavedInputTracker,
  hasOpenModal,
  pageGuardsUnload,
  trackPendingWrites,
  type AppUpdateBusyReason,
} from './guards';

/** Auch dauerhaft sichtbare Tabs (Dispo auf dem zweiten Bildschirm) sollen neue Versionen finden. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
/** Sichtbarer Tab ohne Maus, Tastatur und Scrollen — ab dann gilt er als unbeaufsichtigt. */
const IDLE_AFTER_MS = 10 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];

let controller: AppUpdateController | null = null;

/** Seitenwechsel in der App (components/AppUpdateOnNavigation). */
export function notifyAppNavigation(): void {
  controller?.trigger('navigation');
}

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.log('[main] Service Workers not supported');
    return;
  }

  const pendingWrites = trackPendingWrites(window);
  const unsavedInput = createUnsavedInputTracker();
  const recordInput = (event: Event) => unsavedInput.record(event.target);
  document.addEventListener('input', recordInput, true);
  document.addEventListener('change', recordInput, true);

  const busyReason = (): AppUpdateBusyReason | null => {
    // Offline käme nur die alte Fassung aus dem Cache — und im Keller braucht niemand einen Reload.
    if (!navigator.onLine) return 'offline';
    if (pendingWrites.count() > 0) return 'pending-writes';
    if (unsavedInput.hasUnsavedInput()) return 'unsaved-input';
    if (hasOpenModal(document)) return 'open-modal';
    // Zuletzt, weil es die beforeunload-Handler der Seite auslöst.
    if (pageGuardsUnload(window)) return 'unload-guard';
    return null;
  };

  let registration: ServiceWorkerRegistration | null = null;

  const update = createAppUpdateController({
    busyReason,
    isHidden: () => document.visibilityState === 'hidden',
    serverReachable: async () => {
      // Am Service Worker vorbei (dort ausgenommen): Eine alte 200 aus dem Cache hieße sonst „da".
      const abort = new AbortController();
      const timer = window.setTimeout(() => abort.abort(), 5000);
      try {
        return (await fetch('/api/health', { cache: 'no-store', signal: abort.signal })).ok;
      } catch {
        return false;
      } finally {
        window.clearTimeout(timer);
      }
    },
    activateWaitingWorker: () => {
      const waiting = registration?.waiting;
      if (!waiting) return false;
      waiting.postMessage({ type: 'SKIP_WAITING' });
      return true;
    },
    reload: () => window.location.reload(),
    log: (message) => console.info(`[AppUpdate] ${message}`),
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (handle) => window.clearTimeout(handle as number),
  });
  controller = update;

  // Bekommt eine bisher unkontrollierte Seite ihren ersten Worker (Erst-Installation,
  // clients.claim), ist sie nicht veraltet.
  let currentController = navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const previous = currentController;
    currentController = navigator.serviceWorker.controller;
    if (previous) update.controllerChanged();
  });

  const checkForUpdate = () => {
    void registration?.update().catch(() => {});
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate();
    else update.trigger('hidden');
  });

  let lastActivity = Date.now();
  const markActive = () => {
    lastActivity = Date.now();
  };
  for (const type of ACTIVITY_EVENTS) {
    document.addEventListener(type, markActive, { capture: true, passive: true });
  }
  window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    // Fokus in einem iframe (PDF-, Office-Vorschau): Was dort passiert, sieht dieses Dokument nicht.
    if (document.activeElement?.tagName === 'IFRAME') return;
    if (Date.now() - lastActivity >= IDLE_AFTER_MS) update.trigger('idle');
  }, IDLE_CHECK_INTERVAL_MS);

  try {
    registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      type: 'classic',
      updateViaCache: 'none',
    });
    console.log('[main] Service Worker registered');
  } catch (err) {
    console.error('[main] Service Worker registration failed:', err);
    return;
  }

  const reg = registration;
  // Ein wartender Worker ist nur für eine kontrollierte Seite ein Update — eine unkontrollierte
  // (erster Besuch, Hard-Reload) kam ohnehin frisch vom Netz.
  const noteWaiting = () => {
    if (reg.waiting && navigator.serviceWorker.controller) update.updateFound();
  };
  const watchInstalling = (worker: ServiceWorker) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') noteWaiting();
    });
  };
  noteWaiting();
  if (reg.installing) watchInstalling(reg.installing);
  reg.addEventListener('updatefound', () => {
    if (reg.installing) watchInstalling(reg.installing);
  });

  checkForUpdate();
  window.setInterval(() => {
    if (document.visibilityState === 'visible') checkForUpdate();
  }, UPDATE_CHECK_INTERVAL_MS);
}
